import { FC, useCallback } from 'react';
import { Button, ButtonStyles } from '@silverfort/sf-components';
import { User } from '../../../types/User';
import { usePopup } from '../../../context/PopupProvider';

interface BasePopupParams {
    title: string;
    text: string;
    actionLabel?: string;
    onAction: () => void;
}

interface UseUserActionPopupParams extends BasePopupParams {
    user: User;
}

interface UseUsersActionPopupParams extends BasePopupParams {
    users: User[];
}

interface BaseReturnType {
    closePopup: () => void;
}

interface UseUserActionPopupReturnType extends BaseReturnType {
    openUserActionPopup: () => void;
}

interface UseUsersActionPopupReturnType extends BaseReturnType {
    openUsersActionPopup: () => void;
}

interface ContentProps {
    text: string;
}

interface FooterProps {
    onCancelClick: () => void;
    onActionClick: () => void;
    actionLabel: string;
}

const Content: FC<ContentProps> = ({ text }) => <span className="font-sf">{text}</span>;

const Footer: FC<FooterProps> = ({ onActionClick, onCancelClick, actionLabel }) => (
    <div className="flex gap-2">
        <Button text="Cancel" buttonStyle={ButtonStyles.GhostSolid} onClick={onCancelClick} />
        <Button text={actionLabel} onClick={onActionClick} />
    </div>
);

export const useUserActionPopup = ({
    user,
    onAction,
    title,
    text,
    actionLabel = 'Done'
}: UseUserActionPopupParams): UseUserActionPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    const openUserActionPopup = useCallback(() => {
        openPopup({
            title,
            popupClassName: 'w-[360px]',
            ContentComponent: Content,
            ContentComponentProps: { text },
            FooterComponent: Footer,
            FooterComponentProps: {
                onCancelClick: closePopup,
                onActionClick: onAction,
                actionLabel
            }
        });
    }, [user]);

    return {
        openUserActionPopup,
        closePopup
    };
};

export const useUsersActionPopup = ({
    users,
    onAction,
    title,
    text,
    actionLabel = 'Done'
}: UseUsersActionPopupParams): UseUsersActionPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    const openUsersActionPopup = useCallback(() => {
        openPopup({
            title,
            popupClassName: 'w-[360px]',
            ContentComponent: Content,
            ContentComponentProps: { text },
            FooterComponent: Footer,
            FooterComponentProps: {
                onCancelClick: closePopup,
                onActionClick: onAction,
                actionLabel
            }
        });
    }, [users]);

    return {
        openUsersActionPopup,
        closePopup
    };
};
